import { AfterContentChecked, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm, NgModel } from '@angular/forms';
import { Subject } from 'rxjs';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { FormService } from '../../../common/services/form.service';
import { FormCommonDataService } from '../../../common/services/form-common-data.service';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { FormApiCategory } from '../../../common/models/form-api-category.model';
import { Field } from 'src/sb-tools/typescript/models/field.model';
import { FormCategoryPreselectionService } from '../../../common/services/form-category-preselection.service';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { ClientField } from '../../../common/models/client-field.model';
import { CategoryListItem } from '../../../common/models/category-list-item.model';
import { getCategoryById } from '../../../common/helpers/get-category-by-id';
import { SelectAccessibilityDirective } from '@form-field-module/common/directives/select-accessibility.directive';
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';
import { FocusDirective } from '@sb-tools/typescript/directives/focus.directive';
import { NgClass } from '@angular/common';
import { ProhibitedCategoryValidatorDirective } from '@form-field-module/common/directives/prohibited-category-validator.directive';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'sb-categories',
    templateUrl: './categories.component.html',
    styleUrls: ['./categories.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [
        NgSelectModule,
        SelectAccessibilityDirective,
        FormsModule,
        LoaderComponent,
        FocusDirective,
        NgClass,
        ProhibitedCategoryValidatorDirective,
        TranslateModule,
    ],
})
export class CategoriesComponent implements OnInit, AfterContentChecked, OnDestroy {
    @Input() formField: Field | ClientField;
    @Input() formType: FormType;

    @ViewChild('selectElement') selectElement: NgSelectComponent;

    categoriesList: CategoryListItem[] = [];
    isMobileOrTabletDevice = HelperService.isMobileOrTabletDevice;
    isStoreLastSelectedValue: boolean;
    loading: boolean;

    private allCategories: FormApiCategory[];
    private isRootCategoryPreventedToSelect: boolean;
    private unsubscriber = new Subject<void>();

    constructor(
        public formService: FormService,
        private formCategoryPreselectionService: FormCategoryPreselectionService,
        private formCommonDataService: FormCommonDataService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.isStoreLastSelectedValue = this.formField.options?.isStoreLastSelectedValue === 'true';

        this.allCategories = this.formCommonDataService.form.categories;
        this.isRootCategoryPreventedToSelect =
            this.formCommonDataService.form.rootCategory && !this.formCommonDataService.form.rootCategory.isClickable;

        if (this.isRootCategoryPreventedToSelect) {
            this.formField.isRequired = true;
        }

        this.processPreselectedCategory();
    }

    ngAfterContentChecked(): void {
        // fix for error: Expression has changed after it was checked. Previous value for 'focusableSelectElement': 'undefined'. Current value: '[object Object]'
        this.changeDetectorRef.detectChanges();
    }

    isInvalid(category: CategoryListItem, index: number): boolean {
        return !category.selectedCategory?.id && this.isRequired(index);
    }

    isRequired(index: number): boolean {
        // condition (i === 1) && !this.categoriesList[i - 1]) used for category with hidden parent
        return (
            (this.formField.isRequired && index === 0) ||
            (this.formField.isRequired && index === 1 && !this.categoriesList[index - 1]) ||
            (index > 0 &&
                this.categoriesList[index - 1] &&
                !this.categoriesList[index - 1].selectedCategory?.isClickable) ||
            (this.categoriesList?.length &&
                this.formField.value === this.formField.rootCategoryId &&
                this.isRootCategoryPreventedToSelect)
        );
    }

    onSelect(category: FormApiCategory, level: number, select: NgModel): void {
        this.categoriesList.splice(level + 1);

        if (category) {
            this.initCategoriesDataForSelectors(category.children, level + 1, null);
            this.formField.value = category.id;

            if (level === 0 && this.isStoreLastSelectedValue) {
                this.preselectSubCategoriesFromLocalStorage(category);
            }
        } else {
            if (this.categoriesList[level - 1]?.selectedCategory?.id) {
                this.formField.value = this.categoriesList[level - 1].selectedCategory.id;
            } else {
                this.formField.value = null;
                this.setRootCategoryAsDefaultValue();
            }
        }

        if (this.isStoreLastSelectedValue) {
            this.setSelectedCategoryRootParentId();
        }

        // force re-validate prohibited category after categoriesList loaded
        select.control.updateValueAndValidity();
    }

    private getValidPreselectedCategoryId(...categoriesIdToCheck: number[]): number {
        return categoriesIdToCheck.find((categoryId) => {
            return getCategoryById(+categoryId, this.allCategories);
        });
    }

    private initCategoriesDataForSelectors(categoriesList, level, preselectedCategory?): void {
        this.categoriesList[level] = {
            categories: categoriesList,
            selectedCategory: preselectedCategory
                ? categoriesList.find((category) => {
                      return category.id === preselectedCategory.id;
                  })
                : null,
        };

        this.setRootCategoryAsDefaultValue();
    }

    private isChildOfParentCategory(childCategoryId: number, parentCategory: FormApiCategory): boolean {
        return !!getCategoryById(childCategoryId, parentCategory.children);
    }

    private preselectCategory(categoryId: number): void {
        const preselectedCategory = getCategoryById(categoryId, this.allCategories);

        if (preselectedCategory) {
            if (this.formField.isHidden && !preselectedCategory?.isClickable) {
                // prevent selecting prohibited category from url or via default value
                this.formField.value = null;
                return;
            }

            this.loading = true;

            let currentCategory = preselectedCategory;
            let categories = [];

            while (currentCategory) {
                categories.push(currentCategory);
                currentCategory = getCategoryById(currentCategory.parentId, this.allCategories);
            }

            categories = categories.reverse();

            this.formField.value = categoryId;

            for (let i = 0; i <= categories.length; ++i) {
                this.initCategoriesDataForSelectors(
                    i === 0 ? this.allCategories : categories[i - 1].children,
                    i,
                    categories[i],
                );
            }

            this.setSelectedCategoryRootParentId();
            this.loading = false;
        }
    }

    private preselectSubCategoriesFromLocalStorage(parentCategory: FormApiCategory): void {
        const preselectedSubCategoryInfo = this.formCategoryPreselectionService.getSubCategoryInfoFromLocalStorage(
            parentCategory.id,
        );

        const preselectedSubCategoryId = +preselectedSubCategoryInfo?.subCategoryId;

        if (
            preselectedSubCategoryInfo?.needPreselection &&
            this.formService.selectedCategoryRootParentId !== preselectedSubCategoryId &&
            preselectedSubCategoryId !== this.formField.rootCategoryId &&
            this.isChildOfParentCategory(preselectedSubCategoryId, parentCategory)
        ) {
            this.preselectCategory(preselectedSubCategoryId);
        }
    }

    private processPreselectedCategory(): void {
        let categoryIdFromLocalStorage;

        // preselect category value from localStorage in case field is not hidden
        if (this.isStoreLastSelectedValue && !this.formField.isHidden) {
            const categoryFromLocalStorage = this.formCategoryPreselectionService.getCategoryFromLocalStorage(
                this.formCommonDataService.form.id,
            );

            if (categoryFromLocalStorage) {
                categoryIdFromLocalStorage = categoryFromLocalStorage;
            }
        }

        const paramsCategoryId = +HelperService.getParameterByName('categoryId');

        // pass category values in getValidPreselectedCategoryId according to priorities
        const validPreselectedCategoryId = this.getValidPreselectedCategoryId(
            categoryIdFromLocalStorage,
            paramsCategoryId,
            this.formCategoryPreselectionService.categoryIdFromUrlStoredInCookies,
            this.formField.value,
        );

        if (validPreselectedCategoryId) {
            this.preselectCategory(+validPreselectedCategoryId);
        } else {
            // need to reset value to prevent sending invalid default category
            this.formField.value = null;

            this.initCategoriesDataForSelectors(this.allCategories, 0, null);
        }
    }

    private setRootCategoryAsDefaultValue(): void {
        if (!this.formField.value && this.formField.rootCategoryId && !this.isRootCategoryPreventedToSelect) {
            this.formField.value = this.formField.rootCategoryId;
        }
    }

    private setSelectedCategoryRootParentId(): void {
        if (this.categoriesList?.length && (this.categoriesList[0] || this.categoriesList[1]).selectedCategory) {
            this.formService.selectedCategoryRootParentId = (
                this.categoriesList[0] || this.categoriesList[1]
            ).selectedCategory.id;
        } else {
            this.formService.selectedCategoryRootParentId = null;
        }
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
